// src/components/HexagonalGameLayout.jsx
import React, { useContext, useState, useEffect } from 'react';
import { GameContext } from '../context/GameContext';

const HexagonalGameLayout = () => {
  const { gameState, socket, playerId } = useContext(GameContext);
  const [trickAnimating, setTrickAnimating] = useState(false);
  const [lastTrickLength, setLastTrickLength] = useState(0);
  const [showFullName, setShowFullName] = useState(null);

  const currentPlayer = gameState.players?.find(p => p.id === playerId);
  const currentPlayerIndex = gameState.players?.findIndex(p => p.id === playerId) || 0;
  const trick = gameState?.state?.trick || [];
  const roundPoints = gameState?.state?.roundPoints || {};
  const bases = gameState?.state?.bases || {};
  const trump = gameState?.state?.trump;
  const trumpRevealed = gameState?.state?.trumpRevealed;
  const bid = gameState?.state?.finalBid;
  const caller = gameState.players?.find(p => p.id === gameState?.state?.caller);
  const currentTurnIndex = gameState.state?.turn;
  const canRevealTrump = gameState.state?.canRevealTrump;

  // Detect when trick completes and trigger animation
  useEffect(() => {
    const totalPlayers = gameState.players?.length || 0;
    if (trick.length === totalPlayers && trick.length > 0 && trick.length !== lastTrickLength) {
      setTrickAnimating(true);
      setTimeout(() => {
        setTrickAnimating(false);
      }, 2000);
    }
    setLastTrickLength(trick.length);
  }, [trick.length, gameState.players?.length, lastTrickLength]);

  const handlePlay = (card) => {
    socket.emit('playCard', { gameId: gameState.id, playerId, card });
  };

  const handleRevealTrump = () => {
    socket.emit('revealTrump', { gameId: gameState.id, playerId });
  };

  // Shorten AI player names
  const getDisplayName = (name) => {
    if (name.startsWith('AI Player ')) {
      const num = name.replace('AI Player ', '');
      return `AI${num}`;
    }
    return name;
  };

  if (!currentPlayer) return null;

  // Reorder players so current player is at index 0 (bottom)
  const reorderedPlayers = [];
  for (let i = 0; i < gameState.players.length; i++) {
    const index = (currentPlayerIndex + i) % gameState.players.length;
    reorderedPlayers.push({ ...gameState.players[index], originalIndex: index });
  }

  // Desktop hexagonal positions for avatars
  const getPlayerPositionDesktop = (relativeIndex, totalPlayers) => {
    if (totalPlayers === 4) {
      const positions = [
        { top: '85%', left: '50%', transform: 'translate(-50%, -50%)' },
        { top: '50%', left: '5%', transform: 'translate(0, -50%)' },
        { top: '8%', left: '50%', transform: 'translate(-50%, 0)' },
        { top: '50%', right: '5%', transform: 'translate(0, -50%)' },
      ];
      return positions[relativeIndex];
    } else {
      const positions = [
        { top: '85%', left: '50%', transform: 'translate(-50%, -50%)' },
        { top: '65%', left: '15%', transform: 'translate(0, -50%)' },
        { top: '30%', left: '15%', transform: 'translate(0, -50%)' },
        { top: '8%', left: '50%', transform: 'translate(-50%, 0)' },
        { top: '30%', right: '15%', transform: 'translate(0, -50%)' },
        { top: '65%', right: '15%', transform: 'translate(0, -50%)' },
      ];
      return positions[relativeIndex];
    }
  };

  // Cards positioned NEAR each player (forms ring around board)
  const getTrickCardPositionDesktop = (relativeIndex, totalPlayers) => {
    if (totalPlayers === 4) {
      const positions = [
        { top: '72%', left: '50%', transform: 'translate(-50%, -50%)' }, // user (bottom)
        { top: '50%', left: '20%', transform: 'translate(-50%, -50%)' }, // left player
        { top: '20%', left: '50%', transform: 'translate(-50%, -50%)' }, // top player
        { top: '50%', right: '20%', transform: 'translate(50%, -50%)' }, // right player
      ];
      return positions[relativeIndex];
    } else {
      const positions = [
        { top: '72%', left: '50%', transform: 'translate(-50%, -50%)' }, // user (bottom)
        { top: '58%', left: '25%', transform: 'translate(-50%, -50%)' }, // bottom-left
        { top: '35%', left: '25%', transform: 'translate(-50%, -50%)' }, // top-left
        { top: '20%', left: '50%', transform: 'translate(-50%, -50%)' }, // top
        { top: '35%', right: '25%', transform: 'translate(50%, -50%)' }, // top-right
        { top: '58%', right: '25%', transform: 'translate(50%, -50%)' }, // bottom-right
      ];
      return positions[relativeIndex];
    }
  };

  // Mobile "Tall U" positions for avatars - MAXIMIZED edges
  const getPlayerPositionMobile = (relativeIndex, totalPlayers) => {
    if (totalPlayers === 4) {
      const positions = [
        null, // User avatar not shown - just cards
        { top: '30%', left: '2px', transform: 'translate(0, -50%)' },
        { top: '5%', left: '50%', transform: 'translate(-50%, 0)' },
        { top: '30%', right: '2px', transform: 'translate(0, -50%)' },
      ];
      return positions[relativeIndex];
    } else {
      const positions = [
        null, // User avatar not shown - just cards
        { top: '55%', left: '2px', transform: 'translate(0, -50%)' },
        { top: '25%', left: '2px', transform: 'translate(0, -50%)' },
        { top: '5%', left: '50%', transform: 'translate(-50%, 0)' },
        { top: '25%', right: '2px', transform: 'translate(0, -50%)' },
        { top: '55%', right: '2px', transform: 'translate(0, -50%)' },
      ];
      return positions[relativeIndex];
    }
  };

  // Mobile: Cards positioned NEAR each player (forms ring around board)
  const getTrickCardPositionMobile = (relativeIndex, totalPlayers) => {
    if (totalPlayers === 4) {
      const positions = [
        { bottom: '26vh', left: '50%', transform: 'translate(-50%, 0)' }, // user (just above hand)
        { top: '30%', left: '90px', transform: 'translate(-50%, -50%)' }, // left player
        { top: '15%', left: '50%', transform: 'translate(-50%, -50%)' }, // top player
        { top: '30%', right: '90px', transform: 'translate(50%, -50%)' }, // right player
      ];
      return positions[relativeIndex];
    } else {
      const positions = [
        { bottom: '26vh', left: '50%', transform: 'translate(-50%, 0)' }, // user (just above hand)
        { top: '50%', left: '90px', transform: 'translate(-50%, -50%)' }, // bottom-left
        { top: '28%', left: '90px', transform: 'translate(-50%, -50%)' }, // top-left
        { top: '15%', left: '50%', transform: 'translate(-50%, -50%)' }, // top
        { top: '28%', right: '90px', transform: 'translate(50%, -50%)' }, // top-right
        { top: '50%', right: '90px', transform: 'translate(50%, -50%)' }, // bottom-right
      ];
      return positions[relativeIndex];
    }
  };

  const trumpRevealMessage = () => {
    if (trumpRevealed) return "Already revealed";
    if (!trump) return "Wait for trump";
    if (trick.length === 0) return "Wait for trick";
    if (currentTurnIndex !== currentPlayerIndex) return "Not your turn";
    return "Can reveal!";
  };

  return (
    <>
      <style>{`
        .player-avatar {
          position: absolute;
          padding: 10px 12px;
          border-radius: 8px;
          font-size: 0.95rem;
          font-weight: 600;
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          border: 3px solid;
          transition: all 0.3s ease;
          min-width: 100px;
          max-width: 120px;
          text-align: center;
          color: white;
        }

        .team-a {
          border-color: #3b82f6;
          background: rgba(59, 130, 246, 0.15);
        }

        .team-b {
          border-color: #ef4444;
          background: rgba(239, 68, 68, 0.15);
        }

        .is-active {
          box-shadow: 0 0 20px 4px #fbbf24, 0 0 40px 8px rgba(251, 191, 36, 0.5);
          border-width: 4px;
          transform: scale(1.05);
        }

        .player-name {
          font-size: 1.1rem;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        /* Name tooltip */
        .name-tooltip {
          position: fixed;
          background: rgba(0, 0, 0, 0.9);
          color: white;
          padding: 8px 12px;
          border-radius: 6px;
          font-size: 0.9rem;
          z-index: 1000;
          pointer-events: none;
          white-space: nowrap;
        }

        /* Trick card animations */
        @keyframes trickComplete {
          0% {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
          50% {
            opacity: 1;
            transform: scale(1.1) translateY(-20px);
          }
          100% {
            opacity: 0;
            transform: scale(0.8) translateY(-60px);
          }
        }

        .trick-card-animating {
          animation: trickComplete 2s ease-out forwards;
        }

        /* Compact badge-style reveal trump button */
        .reveal-trump-badge {
          position: absolute;
          top: 8px;
          right: 8px;
          z-index: 250;
          background: rgba(0, 0, 0, 0.7);
          padding: 6px 10px;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 600;
          border: 2px solid;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .reveal-trump-badge.enabled {
          border-color: #dc2626;
          background: rgba(220, 38, 38, 0.2);
          color: #fca5a5;
          animation: pulse 1.5s infinite;
        }

        .reveal-trump-badge.disabled {
          border-color: #6b7280;
          background: rgba(107, 114, 128, 0.2);
          color: #9ca3af;
          cursor: not-allowed;
          opacity: 0.6;
        }

        /* Mobile responsive styles */
        @media (max-width: 768px) {
          .player-avatar {
            min-width: 70px;
            max-width: 85px;
            padding: 8px 10px;
            font-size: 0.85rem;
          }

          .player-name {
            font-size: 0.95rem;
          }

          .mobile-hand-container {
            max-height: 22vh !important;
            padding: 6px 4px !important;
          }

          .mobile-hand-card {
            transform: scale(0.65);
            margin: 0 -18px !important;
            min-width: 55px !important;
            padding: 14px 16px !important;
            font-size: 1.8rem !important;
          }

          .reveal-trump-badge {
            top: 50px;
            right: 8px;
            font-size: 0.65rem;
            padding: 4px 8px;
          }

        }
      `}</style>

      <div style={{
        position: 'relative',
        width: '100vw',
        height: '100vh',
        background: 'linear-gradient(135deg, #1e5a3e 0%, #2d7a4f 50%, #1e5a3e 100%)',
        overflow: 'hidden'
      }}>
        {/* TOP LEFT: Team Scores & Round Points */}
        <div style={{
          position: 'absolute',
          top: '8px',
          left: '8px',
          backgroundColor: 'rgba(0,0,0,0.7)',
          color: 'white',
          padding: '6px 10px',
          borderRadius: '6px',
          fontSize: '0.75rem',
          fontWeight: '600',
          zIndex: 200
        }}>
          <div style={{ color: '#60a5fa' }}>🔵 A: {bases.A || 0}</div>
          <div style={{ color: '#f87171' }}>🔴 B: {bases.B || 0}</div>
          {roundPoints.A !== undefined && (
            <div style={{ marginTop: '4px', paddingTop: '4px', borderTop: '1px solid rgba(255,255,255,0.2)' }}>
              <span style={{ color: '#60a5fa' }}>🔵{roundPoints.A || 0}</span>
              {' | '}
              <span style={{ color: '#f87171' }}>🔴{roundPoints.B || 0}</span>
              {bid && <span style={{ marginLeft: '4px', opacity: 0.7 }}>/{bid}</span>}
            </div>
          )}
        </div>

        {/* TOP RIGHT: Trump & Bid Info */}
        <div style={{
          position: 'absolute',
          top: '8px',
          right: '8px',
          backgroundColor: 'rgba(0,0,0,0.7)',
          color: 'white',
          padding: '6px 10px',
          borderRadius: '6px',
          fontSize: '0.75rem',
          fontWeight: '600',
          textAlign: 'right',
          zIndex: 200
        }}>
          {trumpRevealed && trump && (
            <div style={{ marginBottom: '3px' }}>
              🎺 <span style={{
                fontSize: '1.1rem',
                color: (trump === '♥' || trump === '♦') ? '#f87171' : 'white'
              }}>{trump}</span>
            </div>
          )}
          {caller && bid && (
            <div style={{ fontSize: '0.7rem' }}>Bid: {bid} ({getDisplayName(caller.name)})</div>
          )}
        </div>

        {/* Compact Reveal Trump Badge - Top Right */}
        <div
          className={`reveal-trump-badge ${canRevealTrump ? 'enabled' : 'disabled'}`}
          onClick={canRevealTrump ? handleRevealTrump : undefined}
          title={trumpRevealMessage()}
        >
          🎺 {canRevealTrump ? 'REVEAL' : trumpRevealMessage()}
        </div>

        {/* Player Avatars - Desktop positions */}
        <div className="desktop-avatars" style={{ display: 'block' }}>
          <style>{`
            @media (max-width: 768px) {
              .desktop-avatars { display: none; }
            }
          `}</style>
          {reorderedPlayers.map((player, relativeIndex) => {
            const position = getPlayerPositionDesktop(relativeIndex, reorderedPlayers.length);
            const isActive = player.originalIndex === currentTurnIndex;
            const teamClass = player.team === 'A' ? 'team-a' : 'team-b';
            const activeClass = isActive ? 'is-active' : '';
            const displayName = getDisplayName(player.name);

            return (
              <div
                key={player.id}
                className={`player-avatar ${teamClass} ${activeClass}`}
                style={position}
                title={player.name}
                onClick={() => setShowFullName(showFullName === player.id ? null : player.id)}
              >
                <div className="player-name">{displayName}</div>
              </div>
            );
          })}
        </div>

        {/* Player Avatars - Mobile positions (excluding user at index 0) */}
        <div className="mobile-avatars" style={{ display: 'none' }}>
          <style>{`
            @media (max-width: 768px) {
              .mobile-avatars { display: block; }
            }
          `}</style>
          {reorderedPlayers.map((player, relativeIndex) => {
            if (relativeIndex === 0) return null; // User avatar handled in hand bar

            const position = getPlayerPositionMobile(relativeIndex, reorderedPlayers.length);
            if (!position) return null;

            const isActive = player.originalIndex === currentTurnIndex;
            const teamClass = player.team === 'A' ? 'team-a' : 'team-b';
            const activeClass = isActive ? 'is-active' : '';
            const displayName = getDisplayName(player.name);

            return (
              <div
                key={player.id}
                className={`player-avatar ${teamClass} ${activeClass}`}
                style={position}
                title={player.name}
                onClick={() => setShowFullName(showFullName === player.id ? null : player.id)}
              >
                <div className="player-name">{displayName}</div>
              </div>
            );
          })}
        </div>

        {/* Name tooltip on tap */}
        {showFullName && (
          <div
            className="name-tooltip"
            style={{
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)'
            }}
            onClick={() => setShowFullName(null)}
          >
            {gameState.players.find(p => p.id === showFullName)?.name}
          </div>
        )}

        {/* Trick Cards - Near each player (Desktop) */}
        <div className="desktop-trick-cards">
          <style>{`
            @media (max-width: 768px) {
              .desktop-trick-cards { display: none; }
            }
          `}</style>
          {trick.map((play, idx) => {
            const isRed = play.card.suit === '♥' || play.card.suit === '♦';
            const playerIndex = gameState.players.findIndex(p => p.id === play.playerId);
            const relativeIndex = (playerIndex - currentPlayerIndex + gameState.players.length) % gameState.players.length;
            const position = getTrickCardPositionDesktop(relativeIndex, gameState.players.length);

            return (
              <div
                key={idx}
                className={trickAnimating ? 'trick-card-animating' : ''}
                style={{
                  position: 'absolute',
                  ...position,
                  backgroundColor: 'white',
                  border: `3px solid ${isRed ? '#dc2626' : '#000'}`,
                  borderRadius: '8px',
                  padding: '16px 20px',
                  fontSize: '2.5rem',
                  fontWeight: 'bold',
                  color: isRed ? '#dc2626' : '#000',
                  boxShadow: '0 8px 16px rgba(0,0,0,0.5)',
                  minWidth: '70px',
                  textAlign: 'center',
                  zIndex: 100
                }}
              >
                {play.card.value}{play.card.suit}
              </div>
            );
          })}
        </div>

        {/* Trick Cards - Near each player (Mobile) */}
        <div className="mobile-trick-cards">
          <style>{`
            .mobile-trick-cards { display: none; }
            @media (max-width: 768px) {
              .mobile-trick-cards { display: block !important; }
            }
          `}</style>
          {trick.map((play, idx) => {
            const isRed = play.card.suit === '♥' || play.card.suit === '♦';
            const playerIndex = gameState.players.findIndex(p => p.id === play.playerId);
            const relativeIndex = (playerIndex - currentPlayerIndex + gameState.players.length) % gameState.players.length;
            const position = getTrickCardPositionMobile(relativeIndex, gameState.players.length);

            return (
              <div
                key={idx}
                className={trickAnimating ? 'trick-card-animating' : ''}
                style={{
                  position: 'absolute',
                  ...position,
                  backgroundColor: 'white',
                  border: `3px solid ${isRed ? '#dc2626' : '#000'}`,
                  borderRadius: '8px',
                  padding: '12px 16px',
                  fontSize: '2rem',
                  fontWeight: 'bold',
                  color: isRed ? '#dc2626' : '#000',
                  boxShadow: '0 6px 12px rgba(0,0,0,0.4)',
                  minWidth: '60px',
                  textAlign: 'center',
                  zIndex: 100
                }}
              >
                {play.card.value}{play.card.suit}
              </div>
            );
          })}
        </div>

        {/* BOTTOM: Your Cards (Purple Bar) with User Avatar integrated */}
        <div className="mobile-hand-container" style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: '#5b4fc6',
          padding: '10px 6px',
          display: 'flex',
          gap: '8px',
          flexWrap: 'nowrap',
          justifyContent: 'center',
          borderTop: '3px solid #7c6fd6',
          zIndex: 150,
          overflowX: 'auto'
        }}>
          {currentPlayer.hand?.map((card, idx) => {
            const isRed = card.suit === '♥' || card.suit === '♦';
            return (
              <button
                key={idx}
                onClick={() => handlePlay(card)}
                className="mobile-hand-card"
                style={{
                  backgroundColor: 'white',
                  border: `3px solid ${isRed ? '#dc2626' : '#000'}`,
                  borderRadius: '8px',
                  padding: '18px 22px',
                  fontSize: '2.5rem',
                  fontWeight: 'bold',
                  color: isRed ? '#dc2626' : '#000',
                  cursor: 'pointer',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                  minWidth: '75px',
                  transition: 'transform 0.2s',
                  position: 'relative',
                  flexShrink: 0
                }}
                onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.95)'}
                onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                {card.value}{card.suit}
              </button>
            );
          })}
        </div>

      </div>
    </>
  );
};

export default HexagonalGameLayout;
